import axios from 'axios';

// Polling the round endpoint is the mechanism of record for a cold round: the
// ActionCable push is only a latency optimisation and is known to go missing.
export const ROUND_POLL_INTERVAL_MS = 2000;
// ~2 minutes, then an honest error beats a spinner that never resolves.
export const ROUND_POLL_MAX_ATTEMPTS = 60;
// One 502 must not abandon a running build; a down endpoint must not be polled for minutes.
export const ROUND_POLL_MAX_CONSECUTIVE_ERRORS = 5;

export function isCancellation(error) {
  return error?.name === 'AbortError'
    || error?.name === 'CanceledError'
    || error?.code === 'ERR_CANCELED';
}

function sleep(ms, signal) {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    let timer = null;
    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Resolves GET /demos/:id/round/:n (or /matches/:uuid/round/:n)
 * to a presigned URL, waiting out a 202 build.
 *
 * Returns { kind } where kind is one of:
 * - 'ready' (with url)
 * - 'processing': a full re-parse is running; minutes, not worth polling
 * - 'gone': 410, nothing left to rebuild the round from
 * - 'gave_up': poll limit or repeated transport errors
 * - 'stale': isCurrent() turned false, a newer load owns the page state
 * - 'aborted': signal aborted
 *
 * onPreparing fires (only while current) once the round is known to be building.
 */
export async function pollRoundUrl({
  demoId,
  basePath = `/demos/${demoId}`,
  round,
  teamId,
  signal,
  isCurrent = () => true,
  onPreparing = () => {},
}) {
  let consecutiveErrors = 0;

  for (let attempt = 0; attempt < ROUND_POLL_MAX_ATTEMPTS; attempt += 1) {
    if (signal?.aborted) return { kind: 'aborted' };
    if (!isCurrent()) return { kind: 'stale' };

    let response;
    try {
      // eslint-disable-next-line no-await-in-loop
      response = await axios({
        method: 'get',
        url: `${basePath}/round/${round}`,
        headers: { 'X-TEAM-ID': teamId },
        signal,
      });
      consecutiveErrors = 0;
    } catch (error) {
      if (isCancellation(error) || signal?.aborted) return { kind: 'aborted' };
      if (!isCurrent()) return { kind: 'stale' };
      if (error.response?.status === 410) return { kind: 'gone' };

      consecutiveErrors += 1;
      console.log(error);
      if (consecutiveErrors >= ROUND_POLL_MAX_CONSECUTIVE_ERRORS) {
        console.error('Giving up on round', round, 'after', consecutiveErrors, 'consecutive errors');
        return { kind: 'gave_up' };
      }
      // eslint-disable-next-line no-await-in-loop
      await sleep(ROUND_POLL_INTERVAL_MS, signal);
      // eslint-disable-next-line no-continue
      continue;
    }

    if (!isCurrent()) return { kind: 'stale' };
    if (response.data?.url) return { kind: 'ready', url: response.data.url };
    if (response.data?.status === 'processing') return { kind: 'processing' };

    onPreparing();
    // eslint-disable-next-line no-await-in-loop
    await sleep(ROUND_POLL_INTERVAL_MS, signal);
  }

  console.error('Gave up waiting for round', round, 'to be prepared');
  return { kind: 'gave_up' };
}

// Best effort: if this does not land, the next view presigns the same object and tries again.
export async function reportRoundMissing({
  demoId, basePath = `/demos/${demoId}`, round, teamId,
}) {
  try {
    await axios({
      method: 'post',
      url: `${basePath}/round/${round}/missing`,
      headers: { 'X-TEAM-ID': teamId },
    });
  } catch (error) {
    console.log('Could not report the missing round artifact:', error.message);
  }
}
